import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Loader2, Check, X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useEmpresaCreate, useEnrichByUrl, useEnrichByCnpj } from '@/api/mutations/empresa';

const SETORES = [
  'saude',
  'educacao',
  'industria',
  'comercio',
  'turismo',
  'imobiliario',
  'automotivo',
  'agro',
  'servicos',
  'tecnologia',
  'financeiro',
  'outro',
];

interface EmpresaCreateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EmpresaCreateModal({ open, onOpenChange }: EmpresaCreateModalProps) {
  const nav = useNavigate();
  const create = useEmpresaCreate();
  const enrichUrl = useEnrichByUrl();
  const enrichCnpj = useEnrichByCnpj();
  const [nome, setNome] = useState('');
  const [setor, setSetor] = useState('outro');
  const [cidade, setCidade] = useState('');
  const [site, setSite] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [fonte, setFonte] = useState('');

  useEffect(() => {
    if (open) {
      setNome('');
      setSetor('outro');
      setCidade('');
      setSite('');
      setCnpj('');
      setFonte('');
    }
  }, [open]);

  async function submit(autoEnrich: boolean) {
    if (!nome.trim()) {
      toast.error('Nome obrigatório');
      return;
    }
    try {
      const empresa = await create.mutateAsync({
        payload: {
          nome: nome.trim(),
          setor: setor as 'outro',
          cidade: cidade.trim() || undefined,
          site: site.trim() || undefined,
          cnpj: cnpj.replace(/\D/g, '') || undefined,
          fonte_descoberta: fonte.trim() || undefined,
        },
      });
      toast.success('Empresa criada');

      if (autoEnrich) {
        if (cnpj && cnpj.replace(/\D/g, '').length === 14) {
          try {
            await enrichCnpj.mutateAsync({ id: empresa.id, cnpj: cnpj.replace(/\D/g, '') });
            toast.success('Enriquecida via CNPJ');
          } catch (e) {
            toast.error('CNPJ falhou: ' + (e instanceof Error ? e.message : 'erro'));
          }
        }
        if (site.trim()) {
          try {
            await enrichUrl.mutateAsync({ id: empresa.id, url: site.trim() });
            toast.success('Enriquecida via URL');
          } catch (e) {
            toast.error('URL falhou: ' + (e instanceof Error ? e.message : 'erro'));
          }
        }
      }
      onOpenChange(false);
      nav(`/empresas/${empresa.id}`);
    } catch (e) {
      toast.error('Falha: ' + (e instanceof Error ? e.message : 'erro'));
    }
  }

  const loading = create.isPending || enrichUrl.isPending || enrichCnpj.isPending;
  const canAutoEnrich = (cnpj && cnpj.replace(/\D/g, '').length === 14) || site.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Nova empresa</h3>

          <div>
            <label className="text-xs font-medium text-muted-foreground">Nome *</label>
            <Input
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Nome da empresa"
              autoFocus
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Setor</label>
              <select
                value={setor}
                onChange={(e) => setSetor(e.target.value)}
                className="block w-full rounded-md border bg-background px-2 py-1.5 text-sm"
              >
                {SETORES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Cidade</label>
              <Input value={cidade} onChange={(e) => setCidade(e.target.value)} placeholder="Avaré" />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Site (opcional)</label>
              <Input
                type="url"
                value={site}
                onChange={(e) => setSite(e.target.value)}
                placeholder="empresa.com.br"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">CNPJ (opcional)</label>
              <Input
                value={cnpj}
                onChange={(e) => setCnpj(e.target.value)}
                placeholder="00.000.000/0001-00"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground">Fonte descoberta</label>
            <Input
              value={fonte}
              onChange={(e) => setFonte(e.target.value)}
              placeholder="ex: pesquisa Avaré, indicação, instagram"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5" />
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => submit(false)}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium hover:bg-muted disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5" />
              )}
              Criar
            </button>
            {canAutoEnrich && (
              <button
                type="button"
                onClick={() => submit(true)}
                disabled={loading}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Sparkles className="h-3.5 w-3.5" />
                )}
                Criar e enriquecer
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
